import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, ExternalLink, Youtube } from "lucide-react";
import { toast } from "sonner";

const MelhoresSitesPage = () => {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  const openYoutubeVideo = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const categories = [
    {
      id: "geradores",
      name: "Geradores de Tags e IA",
      sites: [
        {
          name: "RapidTags",
          description: "Gerador de tags para YouTube e outras plataformas",
          url: "https://rapidtags.io/generator",
        },
        {
          name: "Abacus AI",
          description: "Plataforma de chat com IA avançada",
          url: "https://chatllm.abacus.ai/",
        },
        {
          name: "Golex AI",
          description: "Plataforma de criação com inteligência artificial",
          url: "https://golex.ai/pt-BR/webapps/gp9bx4gs6mz8f1fbolk5l",
        },
        {
          name: "DeepMind Veo",
          description: "Modelo de IA da Google DeepMind",
          url: "https://deepmind.google/models/veo/",
        },
      ],
    },
    {
      id: "gifs",
      name: "GIFs e Imagens",
      sites: [
        {
          name: "Tenor",
          description: "Plataforma de GIFs animados",
          url: "https://tenor.com/pt-BR/search/os-simpsons-gifs",
        },
        {
          name: "Artlist",
          description: "Ferramenta de texto para imagem com IA",
          url: "https://artlist.io/text-to-image-ai",
        },
      ],
    },
    {
      id: "videos",
      name: "Criação de Vídeos",
      sites: [
        {
          name: "OneImageVideo",
          description: "Transforme imagens em vídeos incríveis",
          url: "https://www.oneimagevideo.com/",
        },
        {
          name: "Vheer",
          description: "Converta imagens em vídeos animados",
          url: "https://vheer.com/app/image-to-video",
        },
        {
          name: "Canva",
          description: "Plataforma completa de design gráfico",
          url: "https://www.canva.com/",
        },
      ],
    },
    {
      id: "plataformas",
      name: "Plataformas de Vídeo",
      sites: [
        {
          name: "BitChute - Canal Killer45",
          description: "Canal alternativo de vídeos",
          url: "https://old.bitchute.com/channel/jGs5mLAXFlSu/",
        },
        {
          name: "BitChute - Criar Conta",
          description: "Registre-se usando o link de referência",
          url: "https://www.bitchute.com/accounts/referral/killer45/",
        },
        {
          name: "PayChute",
          description: "Plataforma de pagamentos para criadores",
          url: "https://www.paychute.com/c/ajuda-imediata-agora",
        },
        {
          name: "Rumble - Criar Conta",
          description: "Plataforma alternativa de vídeos",
          url: "https://rumble.com/register/FARIAS/",
        },
        {
          name: "Rumble - Canal TV",
          description: "Canal de TV no Rumble",
          url: "https://rumble.com/c/c-1285082?e9s=src_v1_cbl",
        },
      ],
    },
    {
      id: "outros",
      name: "Outros Sites",
      sites: [
        {
          name: "Resultado Jogo do Bicho",
          description: "Confira os resultados do jogo do bicho",
          url: "https://resultadojogodobicho.net/rio-de-janeiro/",
        },
        {
          name: "Encurta.net",
          description: "Encurtador de links com ganhos",
          url: "https://encurta.net/ref/killer56",
        },
        {
          name: "MegaZone Downloads",
          description: "Os melhores sites da internet completo",
          url: "https://megazonedownloads.comunidades.net/os-melhores-sites-da-internet",
        },
      ],
    },
    {
      id: "lives",
      name: "Lives e Vídeos",
      sites: [
        {
          name: "Live: Caminhando nos Trilhos",
          description: "Transmissão ao vivo - Trilhos e Trem",
          url: "https://www.youtube.com/live/jWG98KGDWwA",
          isVideo: true,
          thumbnail: "https://img.youtube.com/vi/jWG98KGDWwA/hqdefault.jpg"
        },
        {
          name: "Live: Reflexão Diária",
          description: "Transmissão ao vivo - Palavra Viva",
          url: "https://www.youtube.com/live/FeaOY4v65yk",
          isVideo: true,
          thumbnail: "https://img.youtube.com/vi/FeaOY4v65yk/hqdefault.jpg"
        },
        {
          name: "O que aconteceu antes da criação do mundo?",
          description: "Vídeo especial sobre teologia",
          url: "https://www.youtube.com/watch?v=TpCLyrRg6wk",
          isVideo: true,
          thumbnail: "https://img.youtube.com/vi/TpCLyrRg6wk/hqdefault.jpg"
        },
        {
          name: "NOS TRILHOS DO SENHOR",
          description: "Reflexão espiritual com temática ferroviária",
          url: "https://www.youtube.com/watch?v=frvHdwWn_FY",
          isVideo: true,
          thumbnail: "https://img.youtube.com/vi/frvHdwWn_FY/hqdefault.jpg"
        },
        {
          name: "Meditação Diária",
          description: "Momento de reflexão e oração",
          url: "https://www.youtube.com/watch?v=xFur7rNtaPE",
          isVideo: true,
          thumbnail: "https://img.youtube.com/vi/xFur7rNtaPE/hqdefault.jpg"
        },
        {
          name: "Shorts: Momento de Fé",
          description: "Curta mensagem inspiradora",
          url: "https://www.youtube.com/shorts/Jd8xNb8PND8",
          isVideo: true,
          thumbnail: "https://img.youtube.com/vi/Jd8xNb8PND8/hqdefault.jpg"
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Os Melhores Sites da Internet
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Coleção dos melhores sites e ferramentas online para diversos fins, desde criação de conteúdo até plataformas de vídeo alternativas.
          </p>
        </div>

        <Tabs defaultValue="lives" className="w-full">
          <div className="flex justify-center mb-8 overflow-x-auto">
            <TabsList className="grid grid-cols-2 md:grid-cols-6 gap-2">
              {categories.map((category) => (
                <TabsTrigger key={category.id} value={category.id} className="px-4 py-2">
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {categories.map((category) => (
            <TabsContent key={category.id} value={category.id} className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.sites.map((site, index) => (
                  <Card key={index} className="overflow-hidden border border-border">
                    <CardHeader className="bg-muted/50">
                      <CardTitle className="text-xl">{site.name}</CardTitle>
                      <CardDescription>{site.description}</CardDescription>
                    </CardHeader>
                    {site.isVideo && site.thumbnail && (
                      <div className="relative">
                        <img 
                          src={site.thumbnail} 
                          alt={site.name} 
                          className="w-full h-48 object-cover cursor-pointer"
                          onClick={() => openYoutubeVideo(site.url)} 
                        />
                        <div 
                          className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition-colors cursor-pointer"
                          onClick={() => openYoutubeVideo(site.url)}
                        >
                          <Youtube size={48} className="text-red-500" />
                        </div>
                      </div>
                    )}
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <a 
                          href={site.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex items-center text-primary hover:underline"
                        >
                          Visitar Site <ExternalLink size={16} className="ml-1" />
                        </a>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleCopy(site.url)}
                          className={copied === site.url ? "bg-primary/10" : ""}
                        >
                          <Copy size={16} className="mr-1" /> 
                          {copied === site.url ? "Copiado!" : "Copiar Link"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default MelhoresSitesPage;